import { useUser } from '../hooks/useUser';
import { useLanePerformance } from '../hooks/useLanePerformance';

export function LaneAnalysis() {
  const { selectedPlayer } = useUser();
  const { laneData, loading, error } = useLanePerformance(selectedPlayer?.bowler_id || null);

  console.log('LaneAnalysis render:', { 
    selectedPlayer: selectedPlayer?.bowler_name, 
    bowlerId: selectedPlayer?.bowler_id,
    laneDataLength: laneData.length,
    loading,
    error: error?.message
  });

  if (!selectedPlayer) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Lane Analysis
          </h1>
          <p className="text-gray-600">
            Performance breakdown by lane number
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            No Player Selected
          </h2>
          
          <p className="text-gray-600">
            Please select a player to view their lane performance analysis.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Lane Analysis
          </h1>
          <p className="text-gray-600">
            Performance breakdown by lane number for {selectedPlayer.bowler_name}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Lane Analysis
          </h1>
          <p className="text-gray-600">
            Performance breakdown by lane number for {selectedPlayer.bowler_name}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Error Loading Data
          </h2>
          
          <p className="text-gray-600 mb-4">
            {error.message}
          </p>
          
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (laneData.length === 0) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Lane Analysis
          </h1>
          <p className="text-gray-600">
            Performance breakdown by lane number for {selectedPlayer.bowler_name}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            No Lane Data Available
          </h2>
          
          <p className="text-gray-600">
            {selectedPlayer.bowler_name} hasn't bowled on any lanes this season, or lane data is not available.
          </p>
        </div>
      </div>
    );
  }

  const bestLane = laneData[0];
  const worstLane = laneData[laneData.length - 1];

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Lane Analysis
        </h1>
        <p className="text-gray-600">
          Performance breakdown by lane number for {selectedPlayer.bowler_name}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Best Lane Pair</p>
              <p className="text-2xl font-bold text-gray-900">Lanes {bestLane.lane_number}</p>
              <p className="text-sm text-green-600">+{bestLane.performance_difference.toFixed(1)} vs league</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Challenging Lane Pair</p>
              <p className="text-2xl font-bold text-gray-900">Lanes {worstLane.lane_number}</p>
              <p className="text-sm text-red-600">{worstLane.performance_difference.toFixed(1)} vs league</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Lane Pairs Bowled</p>
              <p className="text-2xl font-bold text-gray-900">{laneData.length}</p>
              <p className="text-sm text-gray-500">This season</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lane Performance Table - Desktop */}
      <div className="bg-white rounded-lg shadow overflow-hidden hidden md:block">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Lane Pair Performance Details</h2>
          <p className="text-sm text-gray-600">Sorted by performance difference vs league average</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lane Pair
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Your Average
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  League Average
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Difference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Games Played
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {laneData.map((lane, index) => {
                const isAboveAverage = lane.performance_difference > 0;
                const isSignificant = Math.abs(lane.performance_difference) > 5;
                
                return (
                  <tr key={lane.lane_number} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">
                          Lanes {lane.lane_number}
                        </div>
                        {index === 0 && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Best
                          </span>
                        )}
                        {index === laneData.length - 1 && laneData.length > 1 && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Challenging
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lane.player_average.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lane.league_average.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        isAboveAverage ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {isAboveAverage ? '+' : ''}{lane.performance_difference.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lane.games_played}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${
                              isAboveAverage ? 'bg-green-500' : 'bg-red-500'
                            }`}
                            style={{ 
                              width: `${Math.min(Math.abs(lane.performance_difference) * 2, 100)}%` 
                            }}
                          ></div>
                        </div>
                        <span className={`text-xs font-medium ${
                          isSignificant 
                            ? isAboveAverage ? 'text-green-700' : 'text-red-700'
                            : 'text-gray-500'
                        }`}>
                          {isAboveAverage ? 'Strong' : 'Weak'}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lane Performance Cards - Mobile */}
      <div className="md:hidden space-y-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Lane Pair Performance Details</h2>
          <p className="text-sm text-gray-600 mb-4">Sorted by performance difference vs league average</p>
        </div>
        
        {laneData.map((lane, index) => {
          const isAboveAverage = lane.performance_difference > 0;
          const isSignificant = Math.abs(lane.performance_difference) > 5;
          
          return (
            <div key={lane.lane_number} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Lanes {lane.lane_number}
                  </h3>
                  {index === 0 && (
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Best
                    </span>
                  )}
                  {index === laneData.length - 1 && laneData.length > 1 && (
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Challenging
                    </span>
                  )}
                </div>
                <span className={`text-lg font-bold ${
                  isAboveAverage ? 'text-green-600' : 'text-red-600'
                }`}>
                  {isAboveAverage ? '+' : ''}{lane.performance_difference.toFixed(1)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Your Average</p>
                  <p className="text-lg font-semibold text-gray-900">{lane.player_average.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">League Average</p>
                  <p className="text-lg font-semibold text-gray-500">{lane.league_average.toFixed(1)}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">Games Played: {lane.games_played}</span>
                <span className={`text-sm font-medium ${
                  isSignificant 
                    ? isAboveAverage ? 'text-green-700' : 'text-red-700'
                    : 'text-gray-500'
                }`}>
                  {isAboveAverage ? 'Strong Performance' : 'Needs Work'}
                </span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full ${
                    isAboveAverage ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  style={{ 
                    width: `${Math.min(Math.abs(lane.performance_difference) * 3, 100)}%` 
                  }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Performance Insights */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Strengths</h4>
            <ul className="space-y-1">
              {laneData
                .filter(lane => lane.performance_difference > 2)
                .slice(0, 3)
                .map(lane => (
                  <li key={`strength-${lane.lane_number}`} className="text-sm text-green-600">
                    • Lanes {lane.lane_number}: +{lane.performance_difference.toFixed(1)} above average
                  </li>
                ))}
              {laneData.filter(lane => lane.performance_difference > 2).length === 0 && (
                <li className="text-sm text-gray-500">• No lane pairs significantly above average</li>
              )}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Areas for Improvement</h4>
            <ul className="space-y-1">
              {laneData
                .filter(lane => lane.performance_difference < -2)
                .slice(-3)
                .map(lane => (
                  <li key={`improvement-${lane.lane_number}`} className="text-sm text-red-600">
                    • Lanes {lane.lane_number}: {lane.performance_difference.toFixed(1)} below average
                  </li>
                ))}
              {laneData.filter(lane => lane.performance_difference < -2).length === 0 && (
                <li className="text-sm text-gray-500">• No lane pairs significantly below average</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}